import {Autocomplete, Box, Button, Dialog, DialogContent, FormControl, Grid, MenuItem, TextField, Typography} from '@mui/material';

import {useRef} from "react";
import {useFieldArray, useForm} from "react-hook-form";

import {INGESTION_SITES} from '../../../utils/constants';
import Tooltip from '@mui/material/Tooltip';
import {useEffect, useState} from "react";
import {goldErrorFieldSx, useStyles} from "../../../utils/uiUtils";
import {AddressPicker} from "../../../component/address/AddressPicker";
import {UserPicker} from "../../../component/user/UserPicker";
import {REGISTRATION_FIELDS} from "../../../component/user/UserBadge";

export const ingestionSites = INGESTION_SITES

// goldErrorFieldSx paints the border and the label; the helper text needs the same treatment here, because
// MUI's own .Mui-error rule for it is more specific than a plain .MuiFormHelperText-root override.
const goldErrorSx = {...goldErrorFieldSx, '& .MuiFormHelperText-root.Mui-error': {color: '#FFC540'}};

export const Account = (props) => {
    const contactRef = useRef();
    const addressRef = useRef();
    /*
     * Company Name and Work Location are the only two required boxes on this page that are not inside a
     * badge, and they were left to the browser: a bare `required` on each, which answers with the operating
     * system's own "Please fill out this field." bubble, floating over whatever happens to be beneath it —
     * on this page, over the Work Location box and its tooltip. Reported as: "These 2 fields the mandatory
     * looks odd, Can we do similar to the previous style."
     *
     * They now say it the way every other field on the page does: in gold, under the box. Shown once a Save
     * has been refused and only while the box is still empty, so the message clears itself the moment the
     * operator answers it and no handler has to remember to clear it. `required` stays on both fields, for
     * the star and for aria-required; what stops the browser stepping in first is noValidate on the form,
     * which is where the bubble came from.
     */
    const [saveAttempted, setSaveAttempted] = useState(false);
    const companyNameMissing = saveAttempted && !props.watch('companyName')?.trim();
    const workLocationMissing = saveAttempted && !props.watch('WorkLocation');
    /*
     * The gate the external Save button goes through, exposed to the parent via contactGateRef.
     *
     * Flushing is what it was written for: the operator edits Primary Contact and Contact Address inside
     * badges that only push upstream on Enter, so a Save clicked without pressing Enter first would
     * submit the values from before the edit.
     *
     * Validating is the other half, and it was missing. UserBadge marks Email and Mobile required and
     * draws a star on each, but nothing ever asked it whether they were filled — validate() existed on
     * the ref with no caller — so an empty Mobile submitted as happily as a filled one and the star was
     * decoration. validate() marks every field touched, writes the inline messages, and re-expands the
     * badge if it was collapsed, so the reader is taken to the field that is wrong.
     *
     * The address badge is now validated too, where it used to be flushed and nothing more. The note that
     * stood here said the API accepts a customer with no contact address, so enforcing the stars would
     * refuse a save the back end was happy with. It is not happy: contact_address is built and sent
     * unconditionally, so an untouched block goes up as an address with every part blank, and
     * AddressSerializer refuses that — an address it is given has to be an address. What the operator got
     * was {"address_line_1":["This field may not be blank."], ...} in a red banner across the top of the
     * page, for four boxes that carry a star and were sitting empty a screen-length below it.
     *
     * Reported as: "These should be displayed as second image kind of message not the toast can you fix for
     * those fields", against Email's inline "Email is required". So the four stars are enforced where they
     * are drawn: validate() marks every field touched, writes the message under each empty one, and
     * re-expands the badge if it was collapsed. Nothing that saved before stops saving — a blank or half
     * filled address was already refused, just later and somewhere else.
     *
     * Both badges and the two company boxes are asked before any of the answers is acted on, so the operator
     * sees everything that is wrong in one pass rather than a missing Email, then a missing Postcode.
     *
     * Validate before flushing, and flush only once both pass. The order is not cosmetic: flush() pushes
     * the badge's values up through onChange, which sets them on this form, which rebuilds the `value`
     * object handed back down to the badge — and the badge clears its own errors whenever that value
     * changes, on the reasonable assumption that a new value from outside supersedes what it was showing.
     * Flushing first therefore erased the messages validate() had just written, and the reader got a Save
     * button that did nothing at all.
     */
    if (props.contactGateRef) {
        props.contactGateRef.current = () => {
            setSaveAttempted(true);
            const companyValid = Boolean(props.watch('companyName')?.trim() && props.watch('WorkLocation'));
            // No badge mounted means nothing to object to — fail open rather than block the form.
            const contactValid = contactRef.current?.validate() !== false;
            const addressValid = addressRef.current?.validate() !== false;
            if (!companyValid || !contactValid || !addressValid) return false;
            contactRef.current?.flush();
            addressRef.current?.flush();
            return true;
        };
    }
    const {register, setValue, control, getValues} = useForm(
        {
            defaultValues: {
                domain: [{name: ""}],
            }
        }
    );
    const {fields: anotherDomainField, append} = useFieldArray({
        control,
        name: 'domain'
    })
    const classes = useStyles();

    // Single state to track whether the arkbridge field was set from the SaaS dropdown (locks editing)
    const [companyLocked, setCompanyLocked] = useState(false);
    const [domainopen, setDomainopen] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    // When saasCompanies loads, check if current arkbridge_customer_id matches a SaaS company.
    // If it does, the field was previously set from the dropdown — lock it.
    useEffect(() => {
        if (!props.saasCompanies?.length) return;
        const currentId = props.watch('arkbridge_customer_id');
        if (!currentId) return;
        const match = props.saasCompanies.find(c => String(c.company_id) === String(currentId));
        if (match) {
            setCompanyLocked(true);
            props.setValue('companyName', match.name);
        }
    }, [props.saasCompanies]);

    const updateTwoFAMode = (mode) => {
        props.setValue("two_step_verification", mode);
        props.setTwoFAMode(mode);
    }

    const handlecloseDomain = () => {
        setDomainopen(false);
    }

    const handleopenAnotherDomain = () => {
        append({name: ""})
    }

    const domainSubmit = () => {
        const formValues = getValues();
        if (formValues.domain.every(i => i.name.trim() !== "")) {
            setDomainopen(false);
            props.setValue("domain", formValues.domain);
        }
    }

    let _orderUUID = window?.location?.href?.split("uuid=")[1];

    const handleCompanySelect = (e) => {
        const selectedId = e.target.value;
        if (!selectedId) {
            props.setValue('companyName', '');
            props.setValue('arkbridge_customer_id', '');
            setCompanyLocked(false);
            return;
        }

        const company = (props.saasCompanies || []).find(c => c.company_id === parseInt(selectedId, 10));
        if (company) {
            props.setValue('companyName', company.name);
            props.setValue('arkbridge_customer_id', String(company.company_id));
            setCompanyLocked(true);
        }
    };

    return (
        <Box p={4}>
            <Grid container direction='column' spacing={8}>
                <Grid item>
                    <Grid container direction='row' justifyContent='space-between'>
                        <Grid item xs={8}>
                            <Grid container direction='column' spacing={2}>
                                <Grid item>
                                    <Typography align='left' sx={{color: '#FFFFFF'}}><b>Company</b></Typography>
                                </Grid>
                                {/*
                                  * One field, not two. There used to be a "Link to existing SaaS Company"
                                  * dropdown above a separate "Company Name" text box, which read as asking the
                                  * same thing twice: both are the company's name, and which box to use depended
                                  * on knowing whether the company already exists in ArkBridge — something the
                                  * person filling the form is not expected to know off the top of their head.
                                  *
                                  * freeSolo does both jobs in one box. Type, and matching ArkBridge companies
                                  * are offered; pick one and the account is linked to it (arkbridge_customer_id
                                  * is set, and the helper text says so). Type a name that is not in the list and
                                  * it stays a new company, with no id, exactly as entering it manually did.
                                  * Clearing the box unlinks, which is what the old "-- Do not link --" row was
                                  * for.
                                  *
                                  * The old dropdown also rendered its label on top of that placeholder row,
                                  * because MUI does not shrink an outlined label for an empty value while
                                  * displayEmpty draws content in the input. That overlap goes with the dropdown.
                                  */}
                                <Grid item>
                                    <Tooltip title="Start typing to link an existing ArkBridge company, or enter a new name to create one.">
                                        <Autocomplete
                                            freeSolo
                                            options={props.saasCompanies || []}
                                            value={props.watch('companyName') || ''}
                                            className={classes.selectLabelGreyDiff}
                                            fullWidth={true}
                                            /*
                                             * Options are objects while a typed value is a string, so both shapes
                                             * reach here and both have to yield a label — without the string case
                                             * MUI renders "[object Object]" the moment someone types.
                                             */
                                            getOptionLabel={(option) => (typeof option === 'string' ? option : (option?.name ?? ''))}
                                            isOptionEqualToValue={(option, value) => (
                                                (typeof option === 'string' ? option : option?.name) ===
                                                (typeof value === 'string' ? value : value?.name)
                                            )}
                                            /*
                                             * A company already linked to another account cannot be linked again,
                                             * but the one this account is currently on must stay selectable or
                                             * editing an existing account would clear its own link.
                                             */
                                            getOptionDisabled={(option) => {
                                                const isUsed = (props.usedArkbridgeIds || []).includes(option?.company_id);
                                                const isCurrent = String(option?.company_id) === String(props.watch('arkbridge_customer_id'));
                                                return isUsed && !isCurrent;
                                            }}
                                            onChange={(event, newValue) => {
                                                // An object means a company was picked off the list: link it.
                                                if (newValue && typeof newValue !== 'string') {
                                                    handleCompanySelect({target: {value: newValue.company_id}});
                                                    return;
                                                }
                                                // A string is a name typed and committed, null is the box cleared.
                                                // Either way this is not a linked company any more.
                                                handleCompanySelect({target: {value: ''}});
                                                if (newValue) props.setValue('companyName', newValue);
                                            }}
                                            onInputChange={(event, newInput, reason) => {
                                                // Only actual typing unlinks. Selecting an option also fires this
                                                // with reason 'reset', and acting on that would undo the link
                                                // onChange just made.
                                                if (reason !== 'input') return;
                                                props.setValue('companyName', newInput);
                                                if (props.watch('arkbridge_customer_id')) {
                                                    props.setValue('arkbridge_customer_id', '');
                                                    setCompanyLocked(false);
                                                }
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    required
                                                    autoComplete={'off'}
                                                    id="outlined-basic"
                                                    label={_orderUUID ? "" : 'Company Name'}
                                                    variant="outlined"
                                                    name='companyName'
                                                    error={companyNameMissing}
                                                    sx={companyNameMissing ? goldErrorSx : {}}
                                                    /*
                                                     * One line under the box, whichever it is: the refusal
                                                     * while the box is empty, the link once there is one, and
                                                     * a space otherwise so the Work Location box does not
                                                     * shift up and down as it changes.
                                                     *
                                                     * The link line is guarded on the id as well as on the
                                                     * lock, because clearing the box leaves the lock set for a
                                                     * render and it read "Linked to ArkBridge company
                                                     * undefined".
                                                     */
                                                    helperText={companyNameMissing
                                                        ? 'Company Name is required'
                                                        : (companyLocked && props.watch('arkbridge_customer_id')
                                                            ? `Linked to ArkBridge company ${props.watch('arkbridge_customer_id')}`
                                                            : ' ')}
                                                />
                                            )}
                                        />
                                    </Tooltip>
                                </Grid>
                                <Grid item>
                                    <Tooltip title="Primary ingest site for this company.">
                                        <TextField
                                            name='WorkLocation'
                                            className={classes.selectLabelGreyDiff} id="outlined-basic"
                                            required label={_orderUUID ? "" : "Work Location"}
                                            variant="outlined" fullWidth={true} select
                                            error={workLocationMissing}
                                            sx={workLocationMissing ? goldErrorSx : {}}
                                            helperText={workLocationMissing ? 'Work Location is required' : ' '}
                                            value={props.watch('WorkLocation') || ''}
                                            onChange={(e) => props.setValue('WorkLocation', e.target.value)}>
                                            {ingestionSites.map(ingestionSites =>
                                                <MenuItem key={ingestionSites.key} value={ingestionSites.key}>{ingestionSites.label}</MenuItem>)}
                                        </TextField>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={4}>
                            <Box mt={4}>
                                <UploadAndDisplayImage selectedImage={props.selectedImage}
                                                       setSelectedImage={props.setSelectedImage}></UploadAndDisplayImage>
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>
                {/* Address */}
                <Grid item>
                    <Grid container direction='row' justifyContent='space-between'>
                        <Grid item xs={8}>
                            <Grid container direction='column' spacing={2}>
                                <Grid item>
                                    <input type="hidden" {...props.register('title')}/>
                                    <input type="hidden" {...props.register('first_name')}/>
                                    <input type="hidden" {...props.register('middle_name')}/>
                                    <input type="hidden" {...props.register('last_name')}/>
                                    <input type="hidden" {...props.register('email')}/>
                                    <input type="hidden" {...props.register('phone_number')}/>
                                    <input type="hidden" {...props.register('mobile_number')}/>
                                    <input type="hidden" {...props.register('entra_login')}/>
                                    <UserPicker
                                        ref={contactRef}
                                        label="Primary Contact"
                                        onSave={() => {
                                            contactRef.current?.flush();
                                            props.onSave?.();
                                        }}
                                        value={{
                                            title:         props.watch('title'),
                                            first_name:    props.watch('first_name'),
                                            middle_name:   props.watch('middle_name'),
                                            last_name:     props.watch('last_name'),
                                            email:         props.watch('email'),
                                            phone_number:  props.watch('phone_number'),
                                            mobile_number: props.watch('mobile_number'),
                                            entra_login:   props.watch('entra_login') === true,
                                        }}
                                        onChange={(user) => {
                                            props.setValue('title',         user.title         ?? '');
                                            props.setValue('first_name',    user.first_name    ?? '');
                                            props.setValue('middle_name',   user.middle_name   ?? '');
                                            props.setValue('last_name',     user.last_name     ?? '');
                                            props.setValue('email',         user.email         ?? '');
                                            props.setValue('phone_number',  user.phone_number  ?? '');
                                            props.setValue('mobile_number', user.mobile_number ?? '');
                                            props.setValue('entra_login',   user.entra_login === true);
                                        }}
                                        canToggle={true}
                                        active={true}
                                        existingUsers={props.existingUsers}
                                        // The Primary Contact becomes the company's first portal user, so this
                                        // is the one badge that asks how they sign in - as Add New User does.
                                        fields={REGISTRATION_FIELDS}
                                        /*
                                         * Mobile is the contact number a customer must have; Phone is
                                         * optional. Stated here rather than left to UserBadge's default
                                         * so that the page owns the decision — the API used to require
                                         * phone_number instead, which made a blank Phone a 400 after the
                                         * Save, and nexus-api migration 0033 relaxed it to match this.
                                         */
                                        requiredFields={['email', 'mobile_number']}
                                    />
                                </Grid>
                                <Grid item>
                                    <input type="hidden" {...props.register('address_line_1')}/>
                                    <input type="hidden" {...props.register('address_line_2')}/>
                                    <input type="hidden" {...props.register('postal_code')}/>
                                    <input type="hidden" {...props.register('City')}/>
                                    <input type="hidden" {...props.register('State')}/>
                                    <input type="hidden" {...props.register('Country')}/>
                                    <AddressPicker
                                        ref={addressRef}
                                        label="Contact Address"
                                        onSave={() => {
                                            addressRef.current?.flush();
                                            props.onSave?.();
                                        }}
                                        value={{
                                            address_line_1: props.watch('address_line_1'),
                                            address_line_2: props.watch('address_line_2'),
                                            postal_code:    props.watch('postal_code'),
                                            city:           props.watch('City'),
                                            state:          props.watch('State'),
                                            country:        props.watch('Country'),
                                        }}
                                        onChange={(addr) => {
                                            props.setValue('address_line_1', addr.address_line_1 ?? '');
                                            props.setValue('address_line_2', addr.address_line_2 ?? '');
                                            props.setValue('City',           addr.city           ?? '');
                                            props.setValue('State',          addr.state          ?? '');
                                            props.setValue('Country',        addr.country        ?? '');
                                            props.setValue('postal_code',    addr.postal_code    ?? '');
                                        }}
                                        canToggle={true}
                                        active={true}
                                        existingAddresses={props.existingAddresses}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                {/* Account */}
                <Grid item>
                    <Grid container direction='row' justifyContent='space-between'>
                        <Grid item xs={6}>
                            <Grid container direction='column' spacing={1}>
                                <Grid item mt={4}>
                                    <Typography align='left' style={{color: '#FFFFFF'}}><b>Multi Factor Authentication Method </b></Typography>
                                </Grid>
                                <Grid item>
                                    <Grid container direction='row' justifyContent='space-between'>
                                        <Grid item>
                                            <Typography style={{color: '#C7CCD0'}}>Verify by Email</Typography>
                                        </Grid>
                                        <Grid item>
                                            <Button style={props.twoFAMode == 'verify_by_email' ? {
                                                background: '#f5b51f',
                                                color: 'black',
                                                border: '1px solid black',
                                                cursor: 'pointer'
                                            } : {}} onClick={() => updateTwoFAMode("verify_by_email")} variant='outlined' size='medium' sx={{textTransform: 'none', borderRadius: '30px', backgroundColor: '#3A3E3F', border: '1px solid #EEAB10', color: '#EEAB10'}}>Enable</Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item>
                                    <Grid container direction='row' justifyContent='space-between'>
                                        <Grid item>
                                            <Typography style={{color: '#C7CCD0'}}>Verify by Authenticator</Typography>
                                        </Grid>
                                        <Grid item>
                                            <Button style={props.twoFAMode == 'verify_by_authenticator' ? {
                                                background: '#f5b51f',
                                                color: 'black',
                                                border: '1px solid black',
                                                cursor: 'pointer'
                                            } : {}} onClick={() => updateTwoFAMode("verify_by_authenticator")} variant='outlined' size='medium' sx={{textTransform: 'none', borderRadius: '30px', backgroundColor: '#3A3E3F', border: '1px solid #EEAB10', color: '#EEAB10'}}>Enable</Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Dialog open={domainopen} fullWidth={true} PaperProps={{sx: {borderRadius: "12px", backgroundColor: '#3A3E3F'}}}>
                <form id="addDomain">
                    <DialogContent>
                        <Grid container direction='column' spacing={1}>
                            {anotherDomainField.map((item, index) => {
                                return (
                                    <Grid item key={item.id}>
                                        <Typography align='left' sx={{fontWeight: 400, fontSize: '14px', lineHeight: '17px', color: '#FFFFFF'}}>Domain</Typography>
                                        <TextField className={classes.selectLabelGreyDiff} {...register(`domain.${index}.name`)} id="outlined-basic" variant="outlined" fullWidth={true}/>
                                    </Grid>
                                )
                            })
                            }
                            <Grid item>
                                <Typography align='left' style={{fontWeight: 400, fontSize: '17px', color: '#FFFFFF', lineHeight: '21px', cursor: 'pointer'}} onClick={handleopenAnotherDomain}>+ Add another Domain</Typography>
                            </Grid>
                            <Grid item>
                                <Grid container direction='row' justifyContent='flex-end' spacing={2}>
                                    <Grid item>
                                        <Button variant='text'
                                                onClick={handlecloseDomain}
                                                sx={{
                                                    borderRadius: '20px',
                                                    fontSize: '16px',
                                                    fontWeight: 500,
                                                    color: '#FFFFFF',
                                                    textTransform: 'none',
                                                    cursor: 'pointer'
                                                }}>Cancel</Button>
                                    </Grid>
                                    <Grid item>
                                        <Button form="addDomain" variant='contained'
                                                type='button'
                                                onClick={domainSubmit}
                                                sx={{
                                                    borderRadius: '20px',
                                                    width: '100px',
                                                    height: '41px',
                                                    backgroundColor: '#EEAB10',
                                                    color: '#3A3E3F',
                                                    textTransform: 'none',
                                                    cursor: 'pointer'
                                                }}
                                        >Save</Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </DialogContent>
                </form>
            </Dialog>
        </Box>
    )
}

const UploadAndDisplayImage = (props) => {
    const getBase64 = (file) => {
        return new Promise(resolve => {
            let baseURL = "";
            let reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                baseURL = reader.result;
                resolve(baseURL);
            };
        });
    };
    const handleFileInputChange = e => {
        let file = e.target.files[0];
        getBase64(file)
            .then(result => {
                file["base64"] = result;
                props.setSelectedImage({
                    base64URL: result,
                    file
                });
            })
            .catch(err => {
                console.log(err);
            });
    };

    return (
        <div>
            {
                (props?.selectedImage?.base64URL || props?.selectedImage?.url) && (
                    <div>
                        <img width={200} src={(props?.selectedImage?.base64URL) ? props.selectedImage.base64URL :
                            (props?.selectedImage?.url) ? props?.selectedImage?.url : ''}/>
                        <br/>
                        <label htmlFor="uploadimageFile1" style={{
                            boxShadow: '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
                            textTransform: 'none',
                            fontWeight: '700',
                            background: 'linear-gradient(180deg, #EEAB10 0%, #FFC133 100%)',
                            color: '#3A3E3F',
                            borderRadius: '30px',
                            width: '85px',
                            height: '35px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer'
                        }}>
                            Upload
                        </label>
                        <input id="uploadimageFile1" type='file' name='myImage1' style={{visibility: 'hidden', width: '0px'}} onChange={handleFileInputChange} accept='image/*'/>
                    </div>
                )
            }
            {!(props?.selectedImage?.base64URL || props?.selectedImage?.url) && (
                <>
                    <Box style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                        <label htmlFor="uploadimageFile" style={{
                            width: '170px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginLeft: '45px',
                            height: '170px',
                            border: 'dashed 4px #808384',
                            color: '#909293',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer'
                        }}>
                            <Typography>Upload Image</Typography>
                        </label>
                    </Box>
                    <input id="uploadimageFile" type='file' name='myImage' style={{visibility: 'hidden', width: '0px'}} onChange={handleFileInputChange}/>
                </>
            )
            }
        </div>
    )
}
